import React, { ChangeEvent, useCallback, useEffect, useState, useMemo } from "react";
import {
	Box,
	Button,
	Checkbox,
	DrawerBody,
	DrawerFooter,
	Flex,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Input,
	Link,
	Switch,
	Text,
	Tooltip,
	useDisclosure,
} from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { useTranslation } from "react-i18next";
import { ResourceType, SelectedFocusResources, SelectedResource } from "../FocusResourcePicker/SelectedFocusResources";
import { FocusResourcePickerInput } from "../FocusResourcePicker/FocusResourcePickerInput";
import { FocusDTO, FocusValidationConflictError, createOrUpdateFocus, deleteSavedFocusFromLibrary } from "@/js/api/focus";
import { colours } from "@/utils/themeUtils";
import { LibraryDrawerView } from "..";
import { ConfirmDeleteFocusModal } from "../ConfirmDeleteFocusModal";
import {
	mapStudentStableIdsToQoriaUserIds,
	transformSelectedResourcesToMatchAPIContract,
	transformUpsertResonseToFocusItem,
} from "../utils";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { envConfig } from "@/config";
import { areAnyResourcesLockable, markAllInvalidResources } from "./utils";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { isLockedFocusCompatibleUrl } from "@/utils/urlUtils";
import { captureFocusEvent } from "@/utils/posthogUtils";
import { StudentsAuthReminderModal } from "../StudentsAuthReminderModal";
import { usePersistentStore } from "@/stores/v2/persistentStore";
import { isYoutubeEmbedUrl, isYoutubeNoCookieUrl, makeYoutubeEmbedUrlDisplayName } from "@/js/utils/youtubeUrlUtils";
import { containsYoutubeNoCookie, makeYoutubeNoCookieResource, removeYoutubeNoCookieResource } from "./youtubeEmbedUtils";
import { Notice } from "@/components/Notice";
import { WarningBanner } from "@/components/WarningBanner";

export interface EditingFocus {
	id: string;
	name: string;
	active: boolean;
	resources: SelectedResource[];
	isOnTheFly: boolean;
	/**
	 * Whether or not the focus has "Locked Focus" enabled. Omitted for library items, only included for applied focuses.
	 */
	tabsLocked?: boolean;
	/**
	 * The students this focus is applied to.
	 */
	studentIds: string[];
}

export interface FocusPageProps {
	closeDrawer: () => void;
	/**
	 * The selected focus that is either being edited and saved or edited and applied
	 */
	focusBeingEdited?: EditingFocus;
	/**
	 * Used to set the focus of the editing focus in the parent component. This is used when creating a focus so the parent component can pass a focus to be applied to the select students screen.
	 */
	setEditingFocus: (focus: EditingFocus) => void;
	/**
	 * `classIsActive` is used to determine whether the Apply button should say "Apply Focus" or "Preset Focus" when opened from within the context of a classroom.
	 * TODO: Check if class is Active from selectedClassroomStore state instead of prop drilling.
	 */
	classIsActive?: boolean;
	bubbleAppliedOrUpdatedFocus?: (appliedFocus: FocusDTO) => void;
	jumpToView: (view: LibraryDrawerView) => void;
	openedFromAppliedFocusPill?: boolean;
	/**
	 * This can be preset by the parent for the case when the teacher is kicked back to this page from the student selection page
	 */
	validationError?: FocusValidationConflictError;
	handleUpsertFailure: (error: Error) => void;
	/**
	 * When provided (and non-empty), the teacher has already chosen which students to apply the focus to
	 * upstream (e.g. via selection on the Seat Plan View). In that case the Select Students step is skipped:
	 * the footer shows an "Apply Focus" action that applies straight to these ids (stableIdV1 identifiers).
	 */
	preselectedStudentIds?: string[];
}

export function FocusPage(props: FocusPageProps): JSX.Element {
	const { t } = useTranslation();
	const MAX_FOCUS_NAME_LENGTH = envConfig.MAX_FOCUS_NAME_LENGTH;
	const MAX_FOCUSES_IN_LIBRARY = envConfig.MAX_FOCUSES_IN_LIBRARY;

	const confirmationModal = useDisclosure();
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [isUpserting, setIsUpserting] = useState<boolean>(false);

	const userSessionStore = useUserSessionStore();
	const focusStore = useFocusStore();
	const selectedClassroomStore = useSelectedClassroomStore();

	const { successToast, errorToast } = useToast();

	const [saveFocusToLibrary, setSaveFocusToLibrary] = useState<boolean>(false);
	const [focusName, setFocusName] = useState<string>("");
	const [nameIsDuplicate, setNameIsDuplicate] = useState<boolean>(false);

	const [lockedFocus, setLockedFocus] = useState<boolean>(false);
	const [areResourcesLockable, setAreResourcesLockable] = useState(false);
	const studentsAuthReminderModal = useDisclosure();
	const persistentStore = usePersistentStore();

	const [selectedResources, _setSelectedResources] = useState<SelectedResource[]>([]);
	function setSelectedResources(arg: SelectedResource[] | ((prev: SelectedResource[]) => SelectedResource[])): void {
		if (typeof arg === "function") {
			_setSelectedResources((prev) => formatSelectedResources(arg(prev)));
		} else {
			_setSelectedResources(formatSelectedResources(arg));
		}

		function formatSelectedResources(resources: SelectedResource[]): SelectedResource[] {
			const youtubeEmbedResourceIndexes: number[] = [];
			let youtubeNoCookieResourceIndex: number | null = null;

			for (let i = 0; i < resources.length; i++) {
				const resource = resources[i];

				if (isYoutubeEmbedUrl(resource.resourceValue)) {
					resource.label = t("focusLibrary.websiteLabelPrefix", {
						name: makeYoutubeEmbedUrlDisplayName(resource.resourceValue),
					});
					youtubeEmbedResourceIndexes.push(i);
				} else if (isYoutubeNoCookieUrl(resource.resourceValue)) {
					youtubeNoCookieResourceIndex = i;
				}
			}
			// the no cookie resource is not added by the teacher, it accompanies the youtube embed resource for Focus verdict compatibility
			if (youtubeEmbedResourceIndexes.length > 0 && youtubeNoCookieResourceIndex !== null) {
				resources[youtubeNoCookieResourceIndex].hidden = true;
			}

			return resources;
		}
	}

	const openedFromClassroomPage = !!selectedClassroomStore.stableIdV1;
	const hasPreselectedStudents = (props.preselectedStudentIds?.length ?? 0) > 0;

	// Pre-fill the selected resources from provided focus
	useEffect(() => {
		if (props.focusBeingEdited) {
			setSelectedResources((prev) => {
				return prev.length === 0 ? (props.focusBeingEdited?.resources ?? []) : prev;
			});
		}

		if (props.focusBeingEdited?.tabsLocked) {
			setLockedFocus(true);
		}

		if (props.validationError) {
			setSelectedResources((prev) => markAllInvalidResources(prev, props.validationError as FocusValidationConflictError));
		}
		// setSelectedResources is a wrapper recreated each render; listing it would re-run this prefill on every render
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run when focus/validation inputs change only
	}, [props.focusBeingEdited, props.validationError, focusName]);

	// Pre-fill the name field from provided focus
	useEffect(() => {
		if (props.focusBeingEdited) {
			setFocusName((prev) => {
				return prev === "" ? props.focusBeingEdited?.name || "" : prev;
			});
		}

		if (props.validationError) {
			setNameIsDuplicate(!!props.validationError.duplicatedName);
		}
	}, [props.focusBeingEdited, props.validationError]);

	// Calculate number of resources with openTab: true
	const openTabCount = useMemo(() => {
		return selectedResources.filter((resource) => resource.openTab === true && !resource.hidden).length;
	}, [selectedResources]);

	// Get current tab limit from store - subscribe to changes
	const tabLimit = useClassroomPolicyStore((state) => state.getTabLimit());

	// Check if openTabCount exceeds tabLimit
	const isTabLimitExceeded = useMemo(() => {
		return tabLimit !== null && openTabCount > tabLimit;
	}, [tabLimit, openTabCount]);

	const formIsValid = useCallback(() => {
		const trimmedFocusName = focusName.trim();
		const focusNameIsValid = (trimmedFocusName.length > 0 && trimmedFocusName.length <= MAX_FOCUS_NAME_LENGTH) || !saveFocusToLibrary;
		const focusResourcesAreValid = selectedResources.length > 0 && selectedResources.every((resource) => resource.isValid);
		const allResourcesAreLockedFocusCompatible =
			selectedResources.length > 0 && selectedResources.every((resource) => resource.isLockedFocusCompatible);
		const meetsBasicValidation = focusResourcesAreValid && focusNameIsValid && (!lockedFocus || allResourcesAreLockedFocusCompatible);
		// Form is invalid if tab limit is exceeded
		const tabLimitValid = !isTabLimitExceeded;

		return meetsBasicValidation && tabLimitValid;
	}, [lockedFocus, selectedResources, focusName, saveFocusToLibrary, MAX_FOCUS_NAME_LENGTH, isTabLimitExceeded]);

	const formHasChanged = useCallback(() => {
		if (!props.focusBeingEdited) return true;

		const existingResourceValues = props.focusBeingEdited.resources.map((resource) => resource.resourceValue);

		const allSelectedAlreadyExist = selectedResources.every((resource) => {
			return existingResourceValues.includes(resource.resourceValue);
		});
		const allExistingAreSelected = existingResourceValues.every((resource) => {
			return selectedResources.some((x) => x.resourceValue === resource);
		});
		const anySelectedAndExistingResourcesHaveDifferentOpenTab = selectedResources.some((resource) => {
			const existingResource = props.focusBeingEdited?.resources.find((x) => x.resourceValue === resource.resourceValue);
			return existingResource && existingResource.openTab !== resource.openTab;
		});

		const nameChanged = focusName !== props.focusBeingEdited.name;
		const resourcesChanged = !allSelectedAlreadyExist || !allExistingAreSelected || anySelectedAndExistingResourcesHaveDifferentOpenTab;
		const lockedFocusChanged = selectedClassroomStore.stableIdV1 && lockedFocus !== props.focusBeingEdited.tabsLocked;

		return nameChanged || resourcesChanged || lockedFocusChanged;
	}, [selectedResources, focusName, props.focusBeingEdited, lockedFocus, selectedClassroomStore.stableIdV1]);

	const [submitButtonsDisabled, setSubmitButtonsDisabled] = useState<boolean>(true);

	function handleChangeFocusName(event: ChangeEvent<HTMLInputElement>) {
		setFocusName(event.target.value);
	}

	function handleRemoveResource(resource: SelectedResource) {
		const newSelectedResources = selectedResources.filter((x) => x !== resource);

		const _isYoutubeEmbedUrl = isYoutubeEmbedUrl(resource.resourceValue);
		const isRemainingYoutubeEmbedUrls = newSelectedResources.some((x) => isYoutubeEmbedUrl(x.resourceValue));

		if (_isYoutubeEmbedUrl && !isRemainingYoutubeEmbedUrls) {
			removeYoutubeNoCookieResource(newSelectedResources);
		}

		setSelectedResources(newSelectedResources);
	}

	useEffect(() => {
		const lockable = areAnyResourcesLockable(selectedResources);
		setAreResourcesLockable(lockable);

		if (lockedFocus && !lockable) setLockedFocus(false);
	}, [selectedResources, lockedFocus]);

	function handleToggleLockedFocus(e: ChangeEvent<HTMLInputElement>) {
		setLockedFocus(e.target.checked);
		if (e.target.checked) {
			// force openTab to true for non-hidden resources
			const newSelectedResources = selectedResources.map((x) => ({ ...x, openTab: !x.hidden }));
			setSelectedResources(newSelectedResources);
		}
	}

	// Enable/disable submit buttons based on form changes and validity
	useEffect(() => {
		// Submit button should be disabled if form is invalid or no changes have been made.
		// Exception: When editing an existing focus from the classroom page that is currently
		// active, we only need to check if the form is valid.
		if (!!selectedClassroomStore.stableIdV1 && props.focusBeingEdited) {
			if (props.openedFromAppliedFocusPill) {
				setSubmitButtonsDisabled(!formIsValid() || !formHasChanged());
				return;
			}

			setSubmitButtonsDisabled(!formIsValid());
			return;
		}

		setSubmitButtonsDisabled(!formIsValid() || !formHasChanged());
	}, [
		focusName,
		selectedResources,
		saveFocusToLibrary,
		formIsValid,
		formHasChanged,
		props.focusBeingEdited,
		props.openedFromAppliedFocusPill,
		selectedClassroomStore.stableIdV1,
	]);

	// Automatically assume we are saving to library if opened from My Classes page
	useEffect(() => {
		const focusIsBeingEdited = !!props.focusBeingEdited;
		const focusBeingEditedIsOnTheFly = !!props.focusBeingEdited && props.focusBeingEdited.isOnTheFly;

		setSaveFocusToLibrary(!selectedClassroomStore.stableIdV1 && (!focusIsBeingEdited || focusBeingEditedIsOnTheFly));
		if (props.focusBeingEdited && !props.focusBeingEdited.isOnTheFly) {
			setSaveFocusToLibrary(true);
			setFocusName(props.focusBeingEdited.name);
			setSelectedResources(props.focusBeingEdited.resources);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- setSelectedResources wrapper omitted; see prefill effect above
	}, [props.focusBeingEdited, selectedClassroomStore.stableIdV1]);

	function handleCheckboxChanged(resource: SelectedResource) {
		const newSelectedResources = selectedResources.map((x) => (x === resource ? { ...x, openTab: !x.openTab } : x));
		setSelectedResources(newSelectedResources);
	}

	function handleAddResource(resource: SelectedResource) {
		// do not add duplicate resources
		if (selectedResources.some((x) => x.resourceValue === resource.resourceValue)) return;

		if (resource.resourceType === ResourceType.Url) {
			if (isLockedFocusCompatibleUrl(resource.resourceValue)) {
				resource.isLockedFocusCompatible = true;
			}
		}

		// If this is a YouTube embed url, then a YouTube No Cookie resource must also be present for Focus verdict behaviour to work as expected with the embed.
		// Note: Only one no cookie entry is required, it doesn't actually matter if the video id of the cookie entry matches an embed entry.
		if (resource.resourceType === ResourceType.Url && isYoutubeEmbedUrl(resource.resourceValue)) {
			if (!containsYoutubeNoCookie(selectedResources)) {
				selectedResources.push(makeYoutubeNoCookieResource(resource));
			}
		}

		const newSelectedResources = [...selectedResources, resource];
		setSelectedResources(newSelectedResources);
	}

	function clearForm() {
		setFocusName("");

		setNameIsDuplicate(false);
		setSelectedResources([]);
	}

	function handleUpdateExistingFocusWithoutApplying(id: string): void {
		setIsUpserting(true);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(selectedResources),
			lockedFocus,
			false,
			true,
			userSessionStore.getTeacherId() as string,
			focusName,
			undefined,
			undefined,
			id
		)
			.then((response) => {
				successToast({
					title: t("focusLibrary.focusUpdatedInLibraryTitle"),
					description: t("focusLibrary.focusSavedStartClassDescription"),
					isClosable: true,
				});

				clearForm();
				focusStore.upsertFocusItemInLibrary(transformUpsertResonseToFocusItem(response));

				props.closeDrawer();
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);
			})
			.finally(() => {
				setIsUpserting(false);
			});
	}

	function handleCreateNewFocusWithoutApplying(createAnother: boolean) {
		setIsUpserting(true);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(selectedResources),
			lockedFocus,
			false,
			true,
			userSessionStore.getTeacherId() as string,
			focusName
		)
			.then((response) => {
				successToast({
					title: t("focusLibrary.focusCreatedInLibraryTitle"),
					description: t("focusLibrary.focusSavedStartClassDescription"),
					isClosable: true,
				});

				clearForm();
				focusStore.upsertFocusItemInLibrary(transformUpsertResonseToFocusItem(response));

				if (!createAnother) {
					props.closeDrawer();
					return;
				}
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);
			})
			.finally(() => {
				setIsUpserting(false);
			});
	}

	function handleApplyFocus(saveToLibrary: boolean): void {
		setIsUpserting(true);

		// When the teacher already picked students upstream (e.g. Seat Plan selection), apply to those directly.
		const studentIdsToBeAddedToFocus = hasPreselectedStudents
			? (props.preselectedStudentIds as string[])
			: props.focusBeingEdited?.studentIds || [];

		const userIdsToBeAddedToFocus = mapStudentStableIdsToQoriaUserIds(selectedClassroomStore.students, studentIdsToBeAddedToFocus);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(selectedResources),
			lockedFocus,
			true,
			saveToLibrary,
			userSessionStore.getTeacherId() as string,
			saveToLibrary ? focusName.trim() : undefined,
			selectedClassroomStore.stableIdV1 as string,
			studentIdsToBeAddedToFocus,
			props.focusBeingEdited?.id,
			userIdsToBeAddedToFocus
		)
			.then((response) => {
				successToast({
					title: saveToLibrary ? t("focusLibrary.appliedToClassUpdatedTitle") : t("focusLibrary.appliedToClassTitle"),
					isClosable: true,
				});

				clearForm();

				const focusItem = transformUpsertResonseToFocusItem(response);

				if (saveFocusToLibrary) focusStore.upsertFocusItemInLibrary(focusItem);

				if (props.bubbleAppliedOrUpdatedFocus) props.bubbleAppliedOrUpdatedFocus(focusItem);
				props.closeDrawer();
				//track focus event
				captureFocusEvent(
					!props.classIsActive,
					lockedFocus,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string
				);
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);
			})
			.finally(() => {
				setIsUpserting(false);
			});
	}

	function handleConfirmDelete() {
		if (!props.focusBeingEdited) return;

		setIsDeleting(true);

		deleteSavedFocusFromLibrary(props.focusBeingEdited.id, userSessionStore.getTeacherId() as string)
			.then(() => {
				focusStore.removeFocusItemFromLibrary(props.focusBeingEdited?.id as string);
				successToast({ title: t("focusLibrary.focusDeletedFromLibraryTitle"), isClosable: true });
				props.closeDrawer();
			})
			.catch((e) => {
				console.error("Failed to delete focus", e);
				errorToast({
					title: t("focusLibrary.failedDeleteFocusTitle"),
					description: t("errors.unexpectedError"),
					isClosable: true,
				});
			})
			.finally(() => {
				setIsDeleting(false);
			});
	}

	function renderLeftFooterItem(): JSX.Element {
		if (!props.focusBeingEdited || props.focusBeingEdited.isOnTheFly) {
			return <></>;
		}

		if (props.focusBeingEdited?.active || (props.focusBeingEdited && !!selectedClassroomStore.stableIdV1)) {
			return <Text>{t("focusLibrary.updatesSavedToLibrary")}</Text>;
		}

		return (
			<Button data-testid="delete-focus-button" variant={"danger"} onClick={() => confirmationModal.onOpen()}>
				<ChakraFAIcon icon={"fa-trash-can"} variant="solid" mr={2} boxSize={4} />
				{t("focusLibrary.deleteButton")}
			</Button>
		);
	}

	function jumpToSelectStudentsView() {
		const focusToBeApplied: EditingFocus = {
			id: props.focusBeingEdited?.id as string, // Will be undefined when creating a new focus.
			name: focusName,
			active: false,
			resources: selectedResources,
			tabsLocked: lockedFocus,
			isOnTheFly: !saveFocusToLibrary,
			studentIds: [],
		};

		props.setEditingFocus(focusToBeApplied);
		props.jumpToView(LibraryDrawerView.SelectStudents);
	}

	function handleClickSelectStudents() {
		if (lockedFocus && !persistentStore.focusLockDontRemindAgainAboutStudentAuth) {
			studentsAuthReminderModal.onOpen();
			return;
		}
		jumpToSelectStudentsView();
	}

	function applyFocusToPreselectedStudents() {
		if (lockedFocus && !persistentStore.focusLockDontRemindAgainAboutStudentAuth) {
			studentsAuthReminderModal.onOpen();
			return;
		}
		handleApplyFocus(saveFocusToLibrary);
	}

	function handleClickApplyUpdateFocusButton() {
		if (props.focusBeingEdited && props.focusBeingEdited?.isOnTheFly) {
			handleApplyFocus(false);
			return;
		}

		if (props.focusBeingEdited && !selectedClassroomStore.stableIdV1) {
			handleUpdateExistingFocusWithoutApplying(props.focusBeingEdited?.id);
			return;
		}

		if (selectedClassroomStore.stableIdV1) {
			handleApplyFocus(saveFocusToLibrary);
			return;
		}
	}

	function renderRightFooterItem(): JSX.Element {
		if (!selectedClassroomStore.stableIdV1 && !props.focusBeingEdited) {
			return (
				<>
					<Tooltip
						label={
							focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY - 1
								? t("focusLibrary.maxFocusPlansTooltip", { max: MAX_FOCUSES_IN_LIBRARY })
								: t("focusLibrary.saveFocusAndCreateAnotherTooltip")
						}
					>
						<Button
							data-testid="save-and-create-new-focus-button"
							variant={"outline"}
							mr={15}
							ml={"auto"}
							isDisabled={submitButtonsDisabled || focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY - 1}
							onClick={() => handleCreateNewFocusWithoutApplying(true)}
						>
							<ChakraFAIcon icon={"fa-plus"} variant="solid" mr={3} boxSize={4} />
							{t("focusLibrary.saveAndCreateNew")}
						</Button>
					</Tooltip>
					<Tooltip
						label={
							focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY
								? t("focusLibrary.maxFocusPlansTooltip", { max: MAX_FOCUSES_IN_LIBRARY })
								: t("focusLibrary.saveFocusToLibraryTooltip")
						}
					>
						<Button
							data-testid="save-focus-button"
							isDisabled={submitButtonsDisabled || focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY}
							onClick={() => handleCreateNewFocusWithoutApplying(false)}
						>
							<ChakraFAIcon icon={"fa-floppy-disk"} variant="solid" mr={2} boxSize={4} />
							{t("focusLibrary.saveFocus")}
						</Button>
					</Tooltip>
				</>
			);
		}

		function getActionButtonLabel(): string {
			if (selectedClassroomStore.stableIdV1) {
				if (props.openedFromAppliedFocusPill) return t("focusLibrary.updateFocus");

				if (props.classIsActive) return t("focusLibrary.applyFocus");
				return t("focusLibrary.presetFocus");
			} else {
				if (props.focusBeingEdited) return t("focusLibrary.updateFocus");
				return t("focusLibrary.saveFocus");
			}
		}

		const icon = !selectedClassroomStore.stableIdV1 ? "fa-floppy-disk" : "fa-arrows-to-eye";

		// Students were already chosen upstream (e.g. Seat Plan selection): skip the Select Students step
		// and let the teacher apply the focus straight to those students.
		if (hasPreselectedStudents && !!selectedClassroomStore.stableIdV1 && !props.openedFromAppliedFocusPill) {
			return (
				<>
					<StudentsAuthReminderModal
						continue={() => handleApplyFocus(saveFocusToLibrary)}
						isOpen={studentsAuthReminderModal.isOpen}
						onClose={studentsAuthReminderModal.onClose}
					/>
					<Button
						data-testid="apply-focus-to-preselected-button"
						isDisabled={submitButtonsDisabled}
						onClick={applyFocusToPreselectedStudents}
						isLoading={isUpserting}
					>
						<ChakraFAIcon icon={"fa-arrows-to-eye"} variant="solid" mr={2} boxSize={4} />
						{props.classIsActive ? t("focusLibrary.applyFocus") : t("focusLibrary.presetFocus")}
					</Button>
				</>
			);
		}

		const showSelectStudentsButton = !!selectedClassroomStore.stableIdV1 && !props.openedFromAppliedFocusPill;

		if (showSelectStudentsButton) {
			return (
				<>
					<StudentsAuthReminderModal
						continue={jumpToSelectStudentsView}
						isOpen={studentsAuthReminderModal.isOpen}
						onClose={studentsAuthReminderModal.onClose}
					/>
					<Button data-testid="select-students-to-focus-button" isDisabled={submitButtonsDisabled} onClick={handleClickSelectStudents}>
						{t("focusLibrary.selectStudents")}
						<ChakraFAIcon icon={"fa-arrow-right"} variant="solid" ml={2} boxSize={4} />
					</Button>
				</>
			);
		} else {
			return (
				<Button
					data-testid="apply-or-update-focus-button"
					isDisabled={submitButtonsDisabled}
					onClick={handleClickApplyUpdateFocusButton}
					isLoading={isUpserting}
				>
					<ChakraFAIcon icon={icon} variant="solid" mr={2} boxSize={4} />
					{getActionButtonLabel()}
				</Button>
			);
		}
	}

	function handleBackButtonClicked() {
		props.jumpToView(LibraryDrawerView.LibraryView);
	}

	const drawerHeading = props.focusBeingEdited
		? props.focusBeingEdited.name && !!selectedClassroomStore.stableIdV1 && !focusStore.openedDirectlyToEdit
			? props.focusBeingEdited.name
			: t("focusLibrary.editFocusHeading")
		: t("focusLibrary.createFocusHeading");
	const showBackButton =
		(!!selectedClassroomStore.stableIdV1 && !focusStore.openedDirectlyToEdit) ||
		(!selectedClassroomStore.stableIdV1 && props.focusBeingEdited);

	const editingExistingSavedFocus = !!props.focusBeingEdited && !props.focusBeingEdited.isOnTheFly;
	const showSaveToLibraryCheckbox = !!selectedClassroomStore.stableIdV1 && !props.focusBeingEdited; // Only need to show the checkbox when CREATING a focus from the Classroom page
	const showNameField =
		!selectedClassroomStore.stableIdV1 || // If creating or editing a focus from the My Classes page it will always be saved to the library
		editingExistingSavedFocus || // If the focus is already saved to the library, we need to be able to edit the name
		(showSaveToLibraryCheckbox && saveFocusToLibrary); // If we are creating a new focus from the Classroom page and saving it to the library we need to choose a name.

	return (
		<>
			<Flex bg={colours.gray[100]} px={7} py={7} justifyContent={"space-between"} alignItems={"center"}>
				<Flex alignItems={"center"}>
					{showBackButton && (
						<ChakraFAIcon
							data-testid="go-back-button"
							icon={"fa-arrow-left"}
							variant="solid"
							mr={25}
							boxSize={5}
							cursor={"pointer"}
							onClick={handleBackButtonClicked}
						/>
					)}
					<Text data-testid="drawer-heading" fontSize={"xl"} fontWeight={"medium"}>
						{drawerHeading}
					</Text>
				</Flex>
			</Flex>

			<DrawerBody pt={3}>
				{isTabLimitExceeded && tabLimit !== null && (
					<WarningBanner message={t("focusLibrary.tabLimitBanner", { tabLimit, count: openTabCount })} />
				)}

				{showSaveToLibraryCheckbox && (
					<Tooltip
						label={t("focusLibrary.maxFocusPlansTooltip", { max: MAX_FOCUSES_IN_LIBRARY })}
						isDisabled={focusStore.focusLibrary.length < MAX_FOCUSES_IN_LIBRARY}
					>
						<FormControl mb="24px" isDisabled={focusStore.focusLibrary.length >= MAX_FOCUSES_IN_LIBRARY}>
							<Checkbox
								data-testid="save-to-library-checkbox"
								isChecked={saveFocusToLibrary}
								onChange={() => setSaveFocusToLibrary(!saveFocusToLibrary)}
							>
								{t("focusLibrary.saveToMyLibrary")}
							</Checkbox>
						</FormControl>
					</Tooltip>
				)}

				{showNameField && (
					<FormControl mb="24px" isInvalid={focusName.length > MAX_FOCUS_NAME_LENGTH || nameIsDuplicate} isRequired>
						<Flex justifyContent={"space-between"}>
							<FormLabel fontWeight="medium" mb={1}>
								{t("focusLibrary.nameLabel")}
							</FormLabel>
							<Text fontSize={"sm"} color={"neutrals.300"}>
								{t("focusLibrary.maxCharactersAllowed", { max: MAX_FOCUS_NAME_LENGTH })}
							</Text>
						</Flex>
						<Input
							data-testid="focus-name-input"
							placeholder={t("focusLibrary.namePlaceholder")}
							value={focusName}
							onChange={handleChangeFocusName}
						/>
						<FormErrorMessage fontWeight="regular" mt="4px">
							{nameIsDuplicate && t("focusLibrary.nameDuplicateError")}
							{nameIsDuplicate && focusName.length > MAX_FOCUS_NAME_LENGTH && <br />}
							{focusName.length > MAX_FOCUS_NAME_LENGTH && t("focusLibrary.nameMaxLengthError", { max: MAX_FOCUS_NAME_LENGTH })}
						</FormErrorMessage>
					</FormControl>
				)}

				<FocusResourcePickerInput addResource={handleAddResource} lockedFocus={lockedFocus} />

				<SelectedFocusResources
					selectedResources={selectedResources}
					onRemove={handleRemoveResource}
					onCheckboxChanged={handleCheckboxChanged}
					lockedFocus={lockedFocus}
				/>
				{openedFromClassroomPage && (
					<Box mb={15} mt={5}>
						<Flex alignItems={"center"} gap={3} flexWrap="nowrap">
							<FormControl isDisabled={!areResourcesLockable} display="inline-flex" width="auto">
								<Flex alignItems={"center"} display="inline-flex">
									<Switch
										data-testid="locked-focus-switch"
										id="locked-focus-switch"
										isChecked={lockedFocus}
										onChange={handleToggleLockedFocus}
									/>
									<FormLabel
										htmlFor="locked-focus-switch"
										mt={2}
										mb={2}
										ml={2}
										mr={2}
										fontWeight={500}
										cursor={"pointer"}
										whiteSpace="nowrap"
									>
										{t("focusLibrary.lockToPage")}
									</FormLabel>
								</Flex>
							</FormControl>
							<Tooltip
								minWidth={"300px"}
								hasArrow
								shouldWrapChildren
								label={
									<Box>
										<Text mb={2}>{t("focusLibrary.lockToPageTooltipIntro")}</Text>
										<Text mb={2}>{t("focusLibrary.lockToPageTooltipNote")}</Text>
										<Text mb={2}>
											<ChakraFAIcon icon={"fa-circle-check"} variant="solid" mr={2} boxSize={3} color="#41B658" />
											https://website.com/quiz/science/class1
										</Text>
										<Text>
											<ChakraFAIcon icon={"fa-circle-xmark"} variant="solid" mr={2} boxSize={3} color="#CC3746" />
											https://website.com
										</Text>
									</Box>
								}
							>
								<Box display="inline-block" alignSelf="center">
									<ChakraFAIcon icon="fa-question-circle" variant="solid" color={colours.blueGray[400]} width="0.8em" height="0.8em" />
								</Box>
							</Tooltip>
						</Flex>
						<FormControl isDisabled={!areResourcesLockable}>
							<FormHelperText mt={-0.5} opacity={0.8}>
								{t("focusLibrary.lockToPageHelper")}
								<br />
								<Link
									href="https://help.classwize.net/hc/en-gb/articles/21065900439708-Will-Locked-Focus-work-on-my-students"
									target="_blank"
								>
									{t("focusLibrary.findOutMore")}
								</Link>
							</FormHelperText>
						</FormControl>
						{lockedFocus && (
							<Box mb="10px" mt={4}>
								<Notice status="info">{t("focusLibrary.lockToPageDeviceNotice")}</Notice>
							</Box>
						)}
					</Box>
				)}
			</DrawerBody>
			<DrawerFooter bg={colours.gray[100]} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
				<Box>{renderLeftFooterItem()}</Box>
				<Box>{renderRightFooterItem()}</Box>
			</DrawerFooter>
			{props.focusBeingEdited && (
				<ConfirmDeleteFocusModal
					name={props.focusBeingEdited.name}
					isDeleting={isDeleting}
					isOpen={confirmationModal.isOpen}
					closeModal={confirmationModal.onClose}
					handleConfirmDelete={handleConfirmDelete}
				/>
			)}
		</>
	);
}
